import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import SplashPage from './pages/SplashPage';
import WaitlistConfirm from './pages/WaitlistConfirm';
import CommunityImpactDashboard from "./pages/public/CommunityImpactDashboard";

// Donor pages
import DonorLanding from './pages/donor/DonorLanding';
import DonorSignup from './pages/donor/DonorSignup';
import DonorLogin from './pages/donor/DonorLogin';
import DonorDashboard from './pages/donor/DonorDashboard';
import BrowseNeeds from './pages/donor/BrowseNeeds';
import AdoptNeed from './pages/donor/AdoptNeed';
import BrowsePublic from './pages/donor/BrowsePublic';
import OrgPublicProfile from './pages/donor/OrgPublicProfile';
import { CartProvider } from './context/CartContext';
import CartCheckout from './pages/donor/CartCheckout';
import PaymentMethodPage from './pages/donor/PaymentMethod';
import BrowseOrgs from './pages/donor/BrowseOrgs';
import SponsorshipCommitment from './pages/donor/SponsorshipCommitment';
import BrowseCauses from "./pages/donor/BrowseCauses";
import CauseDetail from "./pages/donor/CauseDetail"
import CreateImpactNetwork from "./pages/donor/CreateImpactNetwork";
import JoinImpactNetwork from "./pages/donor/JoinImpactNetwork";
import MyImpactNetworks from "./pages/donor/MyImpactNetworks";
import ImpactNetworkDashboard from "./pages/donor/ImpactNetworkDashboard";

// Org pages
import OrgLanding from './pages/org/OrgLanding';
import OrgSignup from './pages/org/OrgSignup';
import OrgLogin from './pages/org/OrgLogin';
import OrgDashboard from './pages/org/OrgDashboard';
import ManageNeeds from './pages/org/ManageNeeds';
import OrgProfile from './pages/org/OrgProfile';
import OrgInviteAccept from './pages/org/OrgInviteAccept';

// Admin pages
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminLogin from './pages/admin/AdminLogin';

// Protected route component
function ProtectedRoute({ children, requiredRole, redirectTo = "/" }) {
  const { currentUser, userRole, loading } = useAuth();

  if (loading) return <div className="loading">Loading...</div>;

  if (!currentUser) {
    return <Navigate to={redirectTo} />;
  }

  if (requiredRole && userRole === null) {
    return <div className="loading">Loading...</div>;
  }

  if (requiredRole && userRole !== requiredRole) {
    return <Navigate to={redirectTo} />;
  }

  return children;
}

function hasAccess() {
  return localStorage.getItem('sg_access') === 'iamsomebody';
}

function App() {
  return (
  <AuthProvider>
    <CartProvider>
    <Router>
        <Routes>
          {/* Donor routes */}
          <Route path="/" element={
  hasAccess() ? <DonorLanding /> : <SplashPage />
} />

          <Route path="/test-orgs" element={<BrowseOrgs />} />
          <Route path="/give" element={<DonorLanding />} />
          <Route path="/give/signup" element={<DonorSignup />} />
          <Route path="/give/login" element={<DonorLogin />} />
          <Route path="/give/browse-public" element={<BrowsePublic needType="supply" />} />
          <Route path="/give/browse-sponsorships" element={<BrowsePublic needType="sponsorship" />} />
          <Route path="/waitlist-confirm" element={<WaitlistConfirm />} />
          <Route path="/o/:orgId" element={<OrgPublicProfile />} />
          <Route path="/give/orgs" element={<BrowseOrgs />} />
          <Route path="/org/invite/:code" element={<OrgInviteAccept />} />
          <Route path="/impact" element={<CommunityImpactDashboard />} />
          <Route path="/give/causes" element={<BrowseCauses />} />
          <Route path="/give/causes/:causeId" element={<CauseDetail />} />
          <Route path="/donor/impact-networks" element={<MyImpactNetworks />} />
          <Route path="/donor/impact-networks/create" element={<CreateImpactNetwork />} />
          <Route path="/join-network/:inviteCode" element={<JoinImpactNetwork />} />
          <Route path="/donor/impact-networks/:networkId" element={<ImpactNetworkDashboard />} />
          <Route path="/give/dashboard" element={
                        <ProtectedRoute requiredRole="donor">
              <DonorDashboard />
            </ProtectedRoute>
          } />
          <Route path="/give/browse" element={
            <ProtectedRoute requiredRole="donor">
              <BrowseNeeds needType="supply" />
            </ProtectedRoute>
          
          } />
          <Route path="/give/sponsorships" element={
            <ProtectedRoute requiredRole="donor">
              <BrowseNeeds needType="sponsorship" />
            </ProtectedRoute>
          } />

          <Route path="/give/sponsorships/:needId/commit" element={
            <ProtectedRoute requiredRole="donor">
              <SponsorshipCommitment />
            </ProtectedRoute>
          } />
          
          
          <Route path="/give/payment-method" element={
  <ProtectedRoute requiredRole="donor">
    <PaymentMethodPage />
  </ProtectedRoute>
} />

<Route path="/give/adopt/:needId" element={
  <ProtectedRoute requiredRole="donor">
    <AdoptNeed />
  </ProtectedRoute>
} />
<Route path="/org/profile" element={
  <ProtectedRoute requiredRole="org">
    <OrgProfile />
  </ProtectedRoute>
 } />
 <Route path="/give/cart" element={
  <ProtectedRoute requiredRole="donor">
    <CartCheckout />
  </ProtectedRoute>
} />
          {/* Org routes */}
          <Route path="/org" element={<OrgLanding />} />
          <Route path="/org/signup" element={<OrgSignup />} />
          <Route path="/org/login" element={<OrgLogin />} />
          <Route path="/org/dashboard" element={
  <ProtectedRoute requiredRole="org" redirectTo="/org">
    <OrgDashboard />
  </ProtectedRoute>
} />
<Route path="/org/needs" element={
  <ProtectedRoute requiredRole="org" redirectTo="/org">
    <ManageNeeds />
  </ProtectedRoute>
} />
<Route path="/org/profile" element={
  <ProtectedRoute requiredRole="org" redirectTo="/org">
    <OrgProfile />
  </ProtectedRoute>
} />

          {/* Admin routes */}
          <Route path="/admin" element={<AdminLogin />} />
          <Route path="/admin/dashboard" element={
            <ProtectedRoute requiredRole="admin">
              <AdminDashboard />
            </ProtectedRoute>
          } />
        </Routes>
      </Router>
    </CartProvider>
  </AuthProvider>
);
}

export default App;